import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {ConfEnv} from "../config/config";

const BETBY_SCRIPT_URL = ConfEnv.URL_BETBY_SCRIPT;
const KILL_SETTLE_MS = 150;

function invokeWidgetTeardown(instance) {
  if (!instance) return;

  try {
    if (typeof instance.kill === "function") {
      instance.kill();
      return;
    }
  } catch (_e) {}

  try {
    if (typeof instance.destroy === "function") {
      instance.destroy();
    }
  } catch (_e) {}

  try {
    if (typeof instance.cleanup === "function") {
      instance.cleanup();
    }
  } catch (_e) {}
}

function clearTargetAttributes(target) {
  if (!target?.isConnected) return;

  target.removeAttribute("data-bt-initialized");
  target.removeAttribute("data-bt-widget-id");
  target.removeAttribute("data-widget-ready");
  target.style.cssText = "";
}

function clearTargetChildren(target) {
  if (!target?.isConnected) return;

  try {
    target.replaceChildren();
  } catch (_e) {
    while (target.firstChild) {
      try {
        target.removeChild(target.firstChild);
      } catch (_removeError) {
        break;
      }
    }
  }

  clearTargetAttributes(target);
}

function waitForKillSettle() {
  return new Promise(resolve => setTimeout(resolve, KILL_SETTLE_MS));
}

// Global state to manage BTRenderer script loading and instances
class BetbyWidgetManager {
  constructor() {
    this.scriptLoaded = false;
    this.scriptLoading = false;
    this.scriptCallbacks = [];
    this.activeWidgets = new Map();
    this.initPromise = null;
  }

  async ensureScriptLoaded() {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${BETBY_SCRIPT_URL}"]`);

      if (existingScript && window.BTRenderer) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      if (existingScript && !window.BTRenderer) {
        // Script exists but not ready, wait for it
        const checkReady = () => {
          if (window.BTRenderer) {
            this.scriptLoaded = true;
            resolve();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
        return;
      }

      // Load new script
      const scriptElement = document.createElement("script");
      scriptElement.src = BETBY_SCRIPT_URL;
      scriptElement.async = true;

      scriptElement.onload = () => {
        if (window.BTRenderer) {
          this.scriptLoaded = true;
          resolve();
        } else {
          reject(new Error("BTRenderer not available after script load"));
        }
      };

      scriptElement.onerror = () => {
        reject(new Error("Failed to load BTRenderer script"));
      };

      document.head.appendChild(scriptElement);
    });

    return this.initPromise;
  }

  async createWidget(widgetId, config) {
    try {
      await this.ensureScriptLoaded();

      // Clean up any existing widget with this ID first
      await this.destroyWidget(widgetId, {clearDom: true});

      if (config.target) {
        clearTargetChildren(config.target);
      }

      await waitForKillSettle();

      // Verify window.BTRenderer is still available
      if (!window.BTRenderer) {
        throw new Error("BTRenderer not available");
      }

      const widgetInstance = new window.BTRenderer();

      // Store the widget instance immediately with metadata
      this.activeWidgets.set(widgetId, {
        instance: widgetInstance,
        target: config.target,
        initialized: false,
        widgetName: config.widgetName,
        createdAt: Date.now(),
      });

      // Add error handling wrapper to the config
      const wrappedConfig = {
        ...config,
        onError: error => {
          // Clean up failed widget using kill() method
          this.destroyWidget(widgetId);
          // Call original error handler
          if (config.onError) {
            config.onError(error);
          }
        },
      };

      // Initialize the widget and await thenable results to catch async failures
      const initializationResult = widgetInstance.initialize(wrappedConfig);
      if (initializationResult && typeof initializationResult.then === "function") {
        await initializationResult;
      }

      // Mark as initialized and store reference for potential kill() calls
      const widgetData = this.activeWidgets.get(widgetId);
      if (widgetData) {
        widgetData.initialized = true;
        widgetData.initializedAt = Date.now();
      }

      return widgetInstance;
    } catch (error) {
      // Clean up on failure - this will call kill() if instance was created
      await this.destroyWidget(widgetId);
      throw error;
    }
  }

  destroyWidgetSync(widgetId, {clearDom = true, target = null} = {}) {
    const widget = this.activeWidgets.get(widgetId);
    const domTarget = target || widget?.target;

    if (widget) {
      this.activeWidgets.delete(widgetId);
      invokeWidgetTeardown(widget.instance);
    }

    if (clearDom && domTarget?.isConnected) {
      clearTargetChildren(domTarget);
    }
  }

  async destroyWidget(widgetId, {clearDom = false} = {}) {
    this.destroyWidgetSync(widgetId, {clearDom});
    await waitForKillSettle();
  }

  destroyAllWidgets() {
    const widgetIds = Array.from(this.activeWidgets.keys());

    for (const widgetId of widgetIds) {
      this.destroyWidget(widgetId);
    }

    this.activeWidgets.clear();
  }

  // Utility method to get info about active widgets (useful for debugging)
  getActiveWidgetsInfo() {
    const widgets = [];
    this.activeWidgets.forEach((widget, widgetId) => {
      widgets.push({
        id: widgetId,
        widgetName: widget.widgetName,
        initialized: widget.initialized,
        createdAt: widget.createdAt,
        initializedAt: widget.initializedAt,
        hasKillMethod: widget.instance && typeof widget.instance.kill === "function",
      });
    });
    return widgets;
  }
}

// Global singleton instance
const widgetManager = new BetbyWidgetManager();

/**
 * Hook for managing Betby widgets with proper cleanup and conflict prevention
 */
export const useBetbyWidgetManager = widgetConfig => {
  const containerElementRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const widgetIdRef = useRef(null);
  const initTimeoutRef = useRef(null);
  const initializingRef = useRef(false);
  const configHashRef = useRef(null);

  // Create a stable hash of the widget config to prevent unnecessary re-initializations
  const getConfigHash = useCallback(config => {
    if (!config) return null;
    return JSON.stringify({
      token: config.token,
      brand_id: config.brand_id,
      lang: config.lang,
      widgetName: config.widgetName,
      themeName: config.themeName,
    });
  }, []);

  // Generate stable widget ID based on widget name only
  const generateWidgetId = useCallback(() => {
    return `betby-widget-${widgetConfig?.widgetName || "unknown"}-${Math.random().toString(36).substr(2, 9)}`;
  }, [widgetConfig?.widgetName]);

  const destroyWidgetSync = useCallback(({clearDom = true} = {}) => {
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }

    const widgetId = widgetIdRef.current;
    const target = containerElementRef.current;

    if (!widgetId) {
      initializingRef.current = false;
      if (clearDom && target?.isConnected) {
        clearTargetChildren(target);
      }
      return;
    }

    widgetIdRef.current = null;
    configHashRef.current = null;
    initializingRef.current = false;

    widgetManager.destroyWidgetSync(widgetId, {clearDom, target});
  }, []);

  const destroyWidget = useCallback(async ({clearDom = false} = {}) => {
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }

    const widgetId = widgetIdRef.current;
    if (!widgetId) {
      initializingRef.current = false;
      return;
    }

    widgetIdRef.current = null;
    setIsInitialized(false);
    configHashRef.current = null;
    initializingRef.current = false;

    await widgetManager.destroyWidget(widgetId, {clearDom});
  }, []);

  const createWidget = useCallback(async () => {
    if (!widgetConfig || !containerElementRef.current || isLoading || initializingRef.current) {
      return;
    }

    // Check if config actually changed
    const newConfigHash = getConfigHash(widgetConfig);
    if (newConfigHash === configHashRef.current && isInitialized) {
      return; // No need to reinitialize
    }

    initializingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      await destroyWidget({clearDom: true});

      // Generate new widget ID for this instance
      const widgetId = generateWidgetId();
      widgetIdRef.current = widgetId;

      // Create the widget configuration with the container as target
      const config = {
        ...widgetConfig,
        target: containerElementRef.current,
        widgetParams: {
          ...widgetConfig.widgetParams,
          onError: error => {
            setError(`Widget error: ${error.message || error}. Please try refreshing the page.`);
            // Call original onError if it exists
            if (widgetConfig.widgetParams?.onError) {
              widgetConfig.widgetParams.onError(error);
            }
          },
        },
      };

      await widgetManager.createWidget(widgetId, config);

      configHashRef.current = newConfigHash;
      setIsInitialized(true);
    } catch (err) {
      setError(err.message || "Failed to initialize widget");
    } finally {
      setIsLoading(false);
      initializingRef.current = false;
    }
  }, [widgetConfig, isLoading, isInitialized, getConfigHash, generateWidgetId, destroyWidget]);

  const setContainerRef = useCallback(
    node => {
      if (containerElementRef.current && !node) {
        // Synchronous teardown so React unmount does not race BetBy DOM cleanup.
        destroyWidgetSync({clearDom: true});
      }

      containerElementRef.current = node;
    },
    [destroyWidgetSync]
  );

  // Initialize widget when config changes - using a more controlled approach
  useEffect(() => {
    if (!widgetConfig || !containerElementRef.current) {
      return;
    }

    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }

    initTimeoutRef.current = setTimeout(() => {
      const newConfigHash = getConfigHash(widgetConfig);
      if (newConfigHash !== configHashRef.current || !isInitialized) {
        createWidget();
      }
    }, 250);

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [widgetConfig, createWidget, getConfigHash, isInitialized]);

  useLayoutEffect(() => {
    if (!widgetConfig) {
      destroyWidgetSync({clearDom: true});
      setIsInitialized(false);
    }
  }, [widgetConfig, destroyWidgetSync]);

  useLayoutEffect(() => {
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      destroyWidgetSync({clearDom: true});
    };
  }, [destroyWidgetSync]);

  const retryInitialization = useCallback(() => {
    if (initializingRef.current) {
      return;
    }

    void destroyWidget({clearDom: true}).then(() => {
      setTimeout(() => {
        if (widgetConfig && containerElementRef.current) {
          createWidget();
        }
      }, 300);
    });
  }, [createWidget, destroyWidget, widgetConfig]);

  const getDebugInfo = useCallback(() => {
    return {
      isLoading,
      error,
      isInitialized,
      widgetId: widgetIdRef.current,
      isInitializing: initializingRef.current,
      configHash: configHashRef.current,
      activeWidgets: widgetManager.getActiveWidgetsInfo(),
    };
  }, [isLoading, error, isInitialized]);

  return {
    containerRef: setContainerRef,
    isLoading,
    error,
    isInitialized,
    retryInitialization,
    setError,
    getDebugInfo,
  };
};

export default useBetbyWidgetManager;
